import {
  Fragment,
  computed,
  defineComponent,
  getCurrentInstance,
  inject,
  nextTick,
  onMounted,
  onUpdated,
  ref,
  watch,
} from 'vue'
import {
  useDocumentVisibility,
  useResizeObserver,
  useWindowFocus,
} from '@vueuse/core'
import { Close, Menu, Plus } from '@jd/jdesign-icons-vue'
import {
  buildProps,
  capitalize,
  definePropType,
  mutable,
  throwError,
} from '@jdesign-vue/utils'
import { EVENT_CODE } from '@jdesign-vue/constants'
import { JdIcon } from '@jdesign-vue/components/icon'
import { useNamespace } from '@jdesign-vue/hooks'
import JdPopover from '@jdesign-vue/components/popover'
import JdScrollbar from '@jdesign-vue/components/scrollbar'
import TabBar from './tab-bar.vue'
import { tabsRootContextKey } from './constants'

import type {
  CSSProperties,
  ExtractPropTypes,
  RendererElement,
  RendererNode,
  VNode,
} from 'vue'
import type { TabsPaneContext } from './constants'
import type { TabPaneName } from './tabs'

interface Scrollable {
  next?: boolean
  prev?: number
}

export const tabNavFollowProps = buildProps({
  panes: {
    type: definePropType<TabsPaneContext[]>(Array),
    default: () => mutable([] as const),
  },
  currentName: {
    type: [String, Number],
    default: '',
  },
  editable: Boolean,
  addable: Boolean,
  followAdd: Boolean,
  type: {
    type: String,
    values: ['card', 'border-card', 'block', ''],
    default: '',
  },
  stretch: Boolean,
  onTabAdd: {
    type: definePropType<() => void>(Function),
    required: true,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    default: () => () => {},
  },
})

export const tabNavEmits = {
  tabClick: (tab: TabsPaneContext, tabName: TabPaneName, ev: Event) =>
    ev instanceof Event,
  tabRemove: (tab: TabsPaneContext, ev: Event) => ev instanceof Event,
}

export type TabNavFollowProps = ExtractPropTypes<typeof tabNavFollowProps>
export type TabNavEmits = typeof tabNavEmits

const COMPONENT_NAME = 'JdTabNavFollow'
const TabNav = defineComponent({
  name: COMPONENT_NAME,
  props: tabNavFollowProps,
  emits: tabNavEmits,
  setup(props, { expose, emit }) {
    const vm = getCurrentInstance()!

    const rootTabs = inject(tabsRootContextKey)
    if (!rootTabs) throwError(COMPONENT_NAME, `<jd-tabs><tab-nav /></jd-tabs>`)

    const ns = useNamespace('tabs')
    const visibility = useDocumentVisibility()
    const focused = useWindowFocus()

    const navScroll$ = ref<HTMLDivElement>()
    const nav$ = ref<HTMLDivElement>()
    const el$ = ref<HTMLDivElement>()

    const tabBarRef = ref<InstanceType<typeof TabBar>>()

    const scrollable = ref<false | Scrollable>(false)
    const navOffset = ref(0)
    const isFocus = ref(false)
    const focusable = ref(true)

    const hiddenTabs = ref<
      Array<
        TabsPaneContext & {
          tabName: string
          tabLabelContent:
            | string
            | VNode<
                RendererNode,
                RendererElement,
                {
                  [key: string]: any
                }
              >[]
          closable: boolean
        }
      >
    >([])
    const visible = ref()

    const sizeName = computed(() =>
      ['top', 'bottom'].includes(rootTabs.props.tabPosition)
        ? 'width'
        : 'height'
    )
    const navStyle = computed<CSSProperties>(() => {
      const dir = sizeName.value === 'width' ? 'X' : 'Y'
      return {
        transform: `translate${dir}(-${navOffset.value}px)`,
      }
    })

    const scrollPrev = () => {
      if (!navScroll$.value) return

      const containerSize =
        navScroll$.value[`offset${capitalize(sizeName.value)}`]
      const currentOffset = navOffset.value

      if (!currentOffset) return

      const newOffset =
        currentOffset > containerSize ? currentOffset - containerSize : 0

      navOffset.value = newOffset
    }

    const scrollNext = () => {
      if (!navScroll$.value || !nav$.value) return

      const navSize = nav$.value[`offset${capitalize(sizeName.value)}`]
      const containerSize =
        navScroll$.value[`offset${capitalize(sizeName.value)}`]
      const currentOffset = navOffset.value
      if (navSize - containerSize <= currentOffset) return
      // compare tab-nav 修改计算过程
      navOffset.value = navSize - containerSize
    }

    // compare tab-nav 新增方法
    const onProxyWheel = (e: WheelEvent) => {
      const { deltaX, deltaY } = e
      if (deltaX > 0 || deltaY > 0) {
        scrollNext()
      } else if (deltaX < 0 || deltaY < 0) {
        scrollPrev()
      }
    }

    const scrollToActiveTab = async () => {
      const nav = nav$.value
      if (!scrollable.value || !el$.value || !navScroll$.value || !nav) return

      await nextTick()

      const activeTab = el$.value.querySelector('.is-active')
      if (!activeTab) return

      const navScroll = navScroll$.value
      const isHorizontal = ['top', 'bottom'].includes(
        rootTabs.props.tabPosition
      )
      const activeTabBounding = activeTab.getBoundingClientRect()
      const navScrollBounding = navScroll.getBoundingClientRect()
      const maxOffset = isHorizontal
        ? nav.offsetWidth - navScrollBounding.width
        : nav.offsetHeight - navScrollBounding.height
      const currentOffset = navOffset.value
      let newOffset = currentOffset

      if (isHorizontal) {
        if (activeTabBounding.left < navScrollBounding.left) {
          newOffset =
            currentOffset - (navScrollBounding.left - activeTabBounding.left)
        }
        if (activeTabBounding.right > navScrollBounding.right) {
          newOffset =
            currentOffset + activeTabBounding.right - navScrollBounding.right
        }
      } else {
        if (activeTabBounding.top < navScrollBounding.top) {
          newOffset =
            currentOffset - (navScrollBounding.top - activeTabBounding.top)
        }
        if (activeTabBounding.bottom > navScrollBounding.bottom) {
          newOffset =
            currentOffset +
            (activeTabBounding.bottom - navScrollBounding.bottom)
        }
      }
      newOffset = Math.max(newOffset, 0)
      navOffset.value = Math.min(newOffset, maxOffset)
    }

    const update = () => {
      if (!nav$.value || !navScroll$.value) return

      props.stretch && tabBarRef.value?.update()

      const navSize = nav$.value[`offset${capitalize(sizeName.value)}`]
      const containerSize =
        navScroll$.value[`offset${capitalize(sizeName.value)}`]
      const currentOffset = navOffset.value

      if (containerSize < navSize) {
        scrollable.value = scrollable.value || {}
        scrollable.value.prev = currentOffset
        scrollable.value.next = currentOffset + containerSize < navSize
        if (navSize - currentOffset < containerSize) {
          navOffset.value = navSize - containerSize
        }
      } else {
        scrollable.value = false
        if (currentOffset > 0) {
          navOffset.value = 0
        }
      }

      // 计算被隐藏的 tabs
      const navScrollBounding = navScroll$.value.getBoundingClientRect()
      const hiddenTabsArray: any[] = []
      const tabs = Array.from(nav$.value.querySelectorAll(`.${ns.e('item')}`))
      props.panes.forEach((pane, index) => {
        const tab = tabs[index]
        if (tab) {
          const tabBounding = tab.getBoundingClientRect()
          const isHorizontal = ['top', 'bottom'].includes(
            rootTabs.props.tabPosition
          )
          const isHidden = isHorizontal
            ? tabBounding.left < navScrollBounding.left ||
              tabBounding.right > navScrollBounding.right
            : tabBounding.top < navScrollBounding.top ||
              tabBounding.bottom > navScrollBounding.bottom
          if (isHidden) {
            const uid = pane.uid
            const disabled = pane.props.disabled
            const tabName = pane.props.name ?? pane.index ?? `${index}`
            const tabLabelContent = pane.slots.label?.() || pane.props.label
            const closable = !disabled && (pane.isClosable || props.editable)
            hiddenTabsArray.push({
              ...pane,
              uid,
              disabled,
              tabName,
              tabLabelContent,
              closable,
            })
          }
        }
      })
      hiddenTabs.value = hiddenTabsArray

      // 判断滚动区域两端是否有被遮盖隐藏的内容
      const isHorizontal = ['top', 'bottom'].includes(
        rootTabs.props.tabPosition
      )
      const hasLeftHidden = isHorizontal ? currentOffset > 0 : false
      const hasRightHidden = isHorizontal
        ? currentOffset + containerSize < navSize
        : false
      const hasTopHidden = !isHorizontal ? currentOffset > 0 : false
      const hasBottomHidden = !isHorizontal
        ? currentOffset + containerSize < navSize
        : false

      // 获取滚动容器元素
      const navScrollElement = navScroll$.value

      // 根据判断结果添加或移除阴影样式
      if (hasLeftHidden) {
        navScrollElement.classList.add(ns.e('shadow-left'))
      } else {
        navScrollElement.classList.remove(ns.e('shadow-left'))
      }

      if (hasRightHidden) {
        navScrollElement.classList.add(ns.e('shadow-right'))
      } else {
        navScrollElement.classList.remove(ns.e('shadow-right'))
      }

      if (hasTopHidden) {
        navScrollElement.classList.add(ns.e('shadow-top'))
      } else {
        navScrollElement.classList.remove(ns.e('shadow-top'))
      }

      if (hasBottomHidden) {
        navScrollElement.classList.add(ns.e('shadow-bottom'))
      } else {
        navScrollElement.classList.remove(ns.e('shadow-bottom'))
      }
    }

    const changeTab = (e: KeyboardEvent) => {
      const code = e.code

      const { up, down, left, right } = EVENT_CODE
      if (![up, down, left, right].includes(code)) return

      // 左右上下键更换tab
      const tabList = Array.from(
        (e.currentTarget as HTMLDivElement).querySelectorAll<HTMLDivElement>(
          '[role=tab]:not(.is-disabled)'
        )
      )
      const currentIndex = tabList.indexOf(e.target as HTMLDivElement)

      let nextIndex: number
      if (code === left || code === up) {
        // left
        if (currentIndex === 0) {
          // first
          nextIndex = tabList.length - 1
        } else {
          nextIndex = currentIndex - 1
        }
      } else {
        // right
        if (currentIndex < tabList.length - 1) {
          // not last
          nextIndex = currentIndex + 1
        } else {
          nextIndex = 0
        }
      }
      tabList[nextIndex].focus({ preventScroll: true }) // 改变焦点元素
      tabList[nextIndex].click() // 选中下一个tab
      setFocus()
    }

    const setFocus = () => {
      if (focusable.value) isFocus.value = true
    }
    const removeFocus = () => (isFocus.value = false)

    watch(visibility, (visibility) => {
      if (visibility === 'hidden') {
        focusable.value = false
      } else if (visibility === 'visible') {
        setTimeout(() => (focusable.value = true), 50)
      }
    })
    watch(focused, (focused) => {
      if (focused) {
        setTimeout(() => (focusable.value = true), 50)
      } else {
        focusable.value = false
      }
    })

    useResizeObserver(el$, update)

    onMounted(() => setTimeout(() => scrollToActiveTab(), 0))

    onUpdated(() => update())

    expose({
      scrollToActiveTab,
      removeFocus,
    })

    watch(
      () => props.panes,
      () => vm.update(),
      { flush: 'post', deep: true }
    )

    return () => {
      const tabs = props.panes.map((pane, index) => {
        const uid = pane.uid
        const disabled = pane.props.disabled
        const tabName = pane.props.name ?? pane.index ?? `${index}`
        const closable = !disabled && (pane.isClosable || props.editable)
        pane.index = `${index}`

        const btnClose = closable ? (
          <JdIcon
            class="is-icon-close"
            data-component-name="Tabs"
            data-event-name="click"
            data-event-content={`删除标签: ${typeof pane.props.label === 'string' ? pane.props.label : '标签'}`}
            // `onClick` not exist when generate dts
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            onClick={(ev: MouseEvent) => emit('tabRemove', pane, ev)}
          >
            <Close />
          </JdIcon>
        ) : null

        const tabLabelContent = pane.slots.label?.() || pane.props.label
        const tabindex = !disabled && pane.active ? 0 : -1

        return (
          <div
            ref={`tab-${uid}`}
            class={[
              ns.e('item'),
              ns.is(rootTabs.props.tabPosition),
              ns.is('active', pane.active),
              ns.is('disabled', disabled),
              ns.is('closable', closable),
              ns.is('focus', isFocus.value),
            ]}
            id={`tab-${tabName}`}
            key={`tab-${uid}`}
            aria-controls={`pane-${tabName}`}
            role="tab"
            aria-selected={pane.active}
            tabindex={tabindex}
            data-component-name="Tabs"
            data-event-name="click"
            data-event-content={typeof pane.props.label === 'string' ? pane.props.label : 'Tabs标签'}
            onFocus={() => setFocus()}
            onBlur={() => removeFocus()}
            onClick={(ev: MouseEvent) => {
              removeFocus()
              emit('tabClick', pane, tabName, ev)
            }}
            onKeydown={(ev: KeyboardEvent) => {
              if (
                closable &&
                (ev.code === EVENT_CODE.delete ||
                  ev.code === EVENT_CODE.backspace)
              ) {
                emit('tabRemove', pane, ev)
              }
            }}
          >
            {props.type === 'block' ? (
              <div class={ns.e('block')}>
                <span class={ns.e('label')}>{tabLabelContent}</span>
                <span class={ns.is('closable', closable)}>{btnClose}</span>
              </div>
            ) : (
              <div>{...[tabLabelContent, btnClose]}</div>
            )}
          </div>
        )
      })

      const scrollStyle = {
        height:
          ['left', 'right'].includes(rootTabs.props.tabPosition) &&
          rootTabs.props.scrollHeight
            ? rootTabs.props.scrollHeight
            : '100%',
      }

      return (
        <Fragment>
          <div
            ref={el$}
            class={[
              ns.e('nav-wrap'),
              ns.is('scrollable', !!scrollable.value),
              ns.is(rootTabs.props.tabPosition),
            ]}
            onWheel={onProxyWheel}
            onMouseleave={scrollToActiveTab}
          >
            <div
              class={ns.e('nav-scroll')}
              style={scrollStyle}
              ref={navScroll$}
            >
              <div
                class={[
                  ns.e('nav'),
                  ns.is(rootTabs.props.tabPosition),
                  ns.is(
                    'stretch',
                    props.stretch &&
                      ['top', 'bottom'].includes(rootTabs.props.tabPosition)
                  ),
                ]}
                ref={nav$}
                style={navStyle.value}
                role="tablist"
                onKeydown={changeTab}
              >
                {...[
                  !props.type ? (
                    <TabBar ref={tabBarRef} tabs={[...props.panes]} />
                  ) : null,
                  tabs,
                ]}
              </div>
            </div>
          </div>
          {scrollable.value ? (
            <JdPopover
              visible={visible.value}
              onUpdate:visible={(newVisible: boolean) => {
                visible.value = newVisible
              }}
              trigger="hover"
              popperClass={ns.m('tabs-popover')}
              width="unset"
            >
              {{
                reference: () => (
                  <span
                    class={[
                      ns.e('item'),
                      ns.is(rootTabs.props.tabPosition),
                      ns.e('follow-new-tab'),
                    ]}
                    tabindex="0"
                  >
                    <JdIcon class={ns.is('icon-menu')}>
                      <Menu />
                    </JdIcon>
                  </span>
                ),
                default: () => (
                  <JdScrollbar maxHeight="200px">
                    {hiddenTabs.value.map((pane) => {
                      return (
                        <div
                          id={`tab-${pane.tabName}`}
                          key={`tab-${pane.uid}`}
                          class={ns.e('tabs-popover-item')}
                          onFocus={() => setFocus()}
                          onBlur={() => removeFocus()}
                          onClick={(ev: MouseEvent) => {
                            removeFocus()
                            emit('tabClick', pane, pane.tabName, ev)
                            visible.value = false
                          }}
                          onKeydown={(ev: KeyboardEvent) => {
                            if (
                              pane.closable &&
                              (ev.code === EVENT_CODE.delete ||
                                ev.code === EVENT_CODE.backspace)
                            ) {
                              emit('tabRemove', pane, ev)
                            }
                          }}
                        >
                          <div>{pane.tabLabelContent}</div>
                          {pane.closable ? (
                            <JdIcon
                              class="is-icon-close"
                              // @ts-ignore
                              onClick={(ev: MouseEvent) => {
                                emit('tabRemove', pane, ev)
                              }}
                            >
                              <Close />
                            </JdIcon>
                          ) : null}
                        </div>
                      )
                    })}
                  </JdScrollbar>
                ),
              }}
            </JdPopover>
          ) : null}
          <span
            class={[
              ns.e('item'),
              ns.is(rootTabs.props.tabPosition),
              ns.e('follow-new-tab'),
            ]}
            tabindex="0"
            onClick={props.onTabAdd}
            onKeydown={(ev: KeyboardEvent) => {
              if (ev.code === EVENT_CODE.enter) props.onTabAdd()
            }}
          >
            <JdIcon class={ns.is('icon-plus')}>
              <Plus />
            </JdIcon>
          </span>
        </Fragment>
      )
    }
  },
})

export type TabNavInstance = InstanceType<typeof TabNav>
export default TabNav
